import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const standards = ["ISO 14001", "EPA", "CPCB", "GDPR", "SOC 2", "AU Agenda 2063"];

function HeroGovernance() {
  const { t } = useTranslation();
  return (
    <section className="relative pt-32 pb-xl overflow-hidden">
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-cream via-background to-surface-container-low" />
      <div className="absolute top-0 left-0 w-full h-full tech-grid opacity-30" />

      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-md items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="font-label-caps text-on-tertiary-container bg-tertiary-fixed px-3 py-1 rounded-full mb-md inline-block">
              {t("governance.hero.eyebrow")}
            </span>
            <h1 className="font-h1 text-[40px] md:text-h1 text-primary mb-md">
              {t("governance.hero.title_a")} <span className="text-gradient">{t("governance.hero.title_accent")}</span>
            </h1>
            <p className="text-body-lg text-on-surface-variant max-w-xl mb-lg">
              {t("governance.hero.intro")}
            </p>
            <div className="flex flex-wrap gap-2">
              {standards.map((s) => (
                <span key={s} className="px-3 py-1.5 rounded-full bg-white border border-outline-variant text-xs font-bold text-primary">
                  {s}
                </span>
              ))}
            </div>
          </motion.div>

          <div className="relative h-[460px] flex items-center justify-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
              className="absolute w-[420px] h-[420px] rounded-full border border-dashed border-brand/40"
            >
              {standards.map((s, i) => {
                const angle = (i / standards.length) * 360;
                return (
                  <div
                    key={s}
                    style={{ transform: `rotate(${angle}deg) translate(210px) rotate(-${angle}deg)` }}
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                  >
                    <motion.div
                      animate={{ rotate: -360 }}
                      transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                      className="bg-white border border-brand/40 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest text-primary shadow-sm"
                    >
                      {s}
                    </motion.div>
                  </div>
                );
              })}
            </motion.div>

            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
              className="absolute w-[300px] h-[300px] rounded-full border border-dotted border-secondary/40"
            />

            <motion.div
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative w-44 h-52 rounded-[40px] bg-gradient-to-br from-brand to-emerald-600 flex items-center justify-center shadow-2xl"
            >
              <div className="absolute inset-2 rounded-[36px] bg-primary-container/20 backdrop-blur" />
              <span className="material-symbols-outlined icon-fill text-white text-7xl relative z-10">verified_user</span>
              <motion.div
                animate={{ scale: [1, 1.4], opacity: [0.5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute inset-0 rounded-[40px] bg-brand/40 -z-0"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pillars() {
  const { t } = useTranslation();
  const items = t("governance.pillars.items", { returnObjects: true }) || [];
  const icons = ["gavel", "policy", "fact_check", "shield_lock"];
  return (
    <section className="max-w-[1280px] mx-auto px-6 py-xl">
      <div className="mb-lg">
        <h2 className="font-h2 text-h2 text-primary">{t("governance.pillars.title")}</h2>
        <div className="w-20 h-1 bg-brand mt-sm" />
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-gutter">
        {items.map((p, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.5, delay: i * 0.08 }}
            className="bg-white p-md rounded-2xl border border-outline-variant hover:border-brand transition-colors group"
          >
            <span className="material-symbols-outlined text-3xl text-brand mb-sm group-hover:scale-110 transition-transform inline-block">
              {icons[i]}
            </span>
            <h4 className="font-h3 text-primary mb-2">{p.title}</h4>
            <p className="text-sm text-on-surface-variant">{p.desc}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function ComplianceTable() {
  const { t } = useTranslation();
  const rows = t("governance.table.rows", { returnObjects: true }) || [];
  const statusLabels = t("governance.table.status_labels", { returnObjects: true }) || {};
  return (
    <section className="bg-primary-container py-xl">
      <div className="max-w-[1280px] mx-auto px-6">
        <div className="text-center mb-lg">
          <span className="text-brand text-label-caps">{t("governance.table.eyebrow")}</span>
          <h2 className="font-h2 text-h2 text-white mt-2">{t("governance.table.title")}</h2>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-zinc-950">
              <tr className="text-zinc-500 text-xs uppercase tracking-widest">
                <th className="px-6 py-4">{t("governance.table.h_region")}</th>
                <th className="px-6 py-4">{t("governance.table.h_framework")}</th>
                <th className="px-6 py-4 text-right">{t("governance.table.h_status")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className={`border-t border-zinc-800 hover:bg-zinc-800/40 transition-colors ${i % 2 === 0 ? "" : "bg-zinc-900/40"}`}>
                  <td className="px-6 py-4 text-white font-bold">{r.region}</td>
                  <td className="px-6 py-4 text-zinc-300 text-sm">{r.framework}</td>
                  <td className="px-6 py-4 text-right">
                    <span
                      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold ${
                        r.status === "Live"
                          ? "bg-brand/20 text-brand"
                          : r.status === "Pilot"
                          ? "bg-yellow-500/20 text-yellow-400"
                          : "bg-zinc-700/40 text-zinc-300"
                      }`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      {statusLabels[r.status] || r.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default function Governance() {
  return (
    <>
      <HeroGovernance />
      <Pillars />
      <ComplianceTable />
    </>
  );
}
